import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Crown, Calendar, CreditCard, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { premiumAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const SubscriptionDetailsPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadSubscriptionDetails();
  }, [isAuthenticated, navigate]);

  const loadSubscriptionDetails = async () => {
    try {
      const data = await premiumAPI.getStatus();
      setPremiumStatus(data);
    } catch (error) {
      console.error('Failed to load subscription:', error);
      toast({
        title: "Error",
        description: "Failed to load subscription details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will lose premium access.')) {
      return;
    }

    try {
      await premiumAPI.cancelSubscription();
      toast({
        title: "Subscription Cancelled",
        description: "Your subscription has been cancelled. Premium features will remain active until the expiry date.",
      });
      // Reload subscription details
      loadSubscriptionDetails();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to cancel subscription. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRenewSubscription = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/premium/renew-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Auto-Renewal Enabled",
          description: data.message || "Your subscription will automatically renew",
        });
        loadSubscriptionDetails();
      } else {
        throw new Error(data.detail || 'Failed to enable auto-renewal');
      }
    } catch (error) {
      console.error('Failed to enable auto-renewal:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to enable auto-renewal. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full px-2">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscription Details</h1>
          <p className="text-gray-600">Manage your premium subscription</p>
        </div>

        {/* Trial Countdown Banner - Show only for trial users */}
        {premiumStatus?.isFreeTrial && premiumStatus?.daysRemaining !== null && (
          <div className={`mb-6 rounded-xl p-4 flex items-center justify-between ${
            premiumStatus.daysRemaining <= 3 
              ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' 
              : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
          }`}>
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6" />
              <div>
                <p className="font-semibold">
                  {premiumStatus.daysRemaining <= 3 
                    ? `Only ${premiumStatus.daysRemaining} day${premiumStatus.daysRemaining !== 1 ? 's' : ''} left!` 
                    : `Your free trial ends in ${premiumStatus.daysRemaining} days`}
                </p>
                <p className="text-sm opacity-90">
                  {premiumStatus.daysRemaining <= 3 
                    ? 'Upgrade now to keep your premium features' 
                    : 'Enjoying all premium features'}
                </p>
              </div>
            </div>
            <Link to="/pricing">
              <Button className="bg-white text-gray-900 hover:bg-gray-100 font-semibold">
                Upgrade Now
              </Button>
            </Link>
          </div>
        )}

        {/* Current Subscription Status */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="w-6 h-6 text-yellow-500" />
              Current Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            {premiumStatus?.isPremium ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{premiumStatus.planName} Plan</h3>
                    <p className="text-gray-600">
                      {premiumStatus.isFreeTrial ? 'Free Trial - Enjoy all premium features!' : 'Active Premium Member'}
                    </p>
                  </div>
                  <Badge className={`${premiumStatus.isFreeTrial ? 'bg-blue-600' : 'bg-green-600'} text-white text-lg px-4 py-2`}>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    {premiumStatus.isFreeTrial ? 'Free Trial' : 'Active'}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                  {premiumStatus.expiryDate && (
                    <div className="flex items-start">
                      <Calendar className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">
                          {premiumStatus.isFreeTrial ? 'Trial Expires On' : 'Expires On'}
                        </p>
                        <p className="font-medium">{new Date(premiumStatus.expiryDate).toLocaleDateString()}</p>
                        {premiumStatus.daysRemaining !== null && premiumStatus.daysRemaining !== undefined && (
                          <p className="text-xs text-gray-500">({premiumStatus.daysRemaining} days remaining)</p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex items-start">
                    <CreditCard className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">Contacts Viewed</p>
                      <p className="font-medium">
                        {premiumStatus.contactsViewed || 0} / {premiumStatus.contactLimit === -1 ? 'Unlimited' : premiumStatus.contactLimit}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  {premiumStatus.isFreeTrial ? (
                    <Link to="/pricing" className="flex-1">
                      <Button className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white">
                        <Crown className="w-5 h-5 mr-2" />
                        Upgrade to Full Premium - $9.99/year
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Link to="/pricing" className="flex-1">
                        <Button variant="outline" className="w-full">
                          Upgrade Plan
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        className="flex-1 text-navy-500 hover:bg-cream-100"
                        onClick={handleCancelSubscription}
                      >
                        Cancel Subscription
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Crown className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {premiumStatus?.freeTrialUsed ? 'Your Free Trial Has Ended' : 'No Active Subscription'}
                </h3>
                <p className="text-gray-500 mb-6">
                  {premiumStatus?.freeTrialUsed 
                    ? 'Upgrade to premium to continue enjoying all features' 
                    : 'Subscribe to a premium plan to unlock all features'}
                </p>
                <Link to="/pricing">
                  <Button className="bg-mint-500 hover:bg-mint-600">
                    <Crown className="w-5 h-5 mr-2" />
                    {premiumStatus?.freeTrialUsed ? 'Upgrade to Premium' : 'View Premium Plans'}
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Billing History */}
        <Card>
          <CardHeader>
            <CardTitle>Billing History</CardTitle>
          </CardHeader>
          <CardContent>
            {premiumStatus?.isPremium ? (
              <div className="space-y-4">
                {/* Mock billing history - Replace with actual data */}
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="font-medium">{premiumStatus.planName} Plan - 1 Month</p>
                    <p className="text-sm text-gray-600">{new Date(premiumStatus.startDate).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">
                      {premiumStatus.planName === 'Silver' ? '$2.99' : 
                       premiumStatus.planName === 'Gold' ? '$2.99' : '$2.99'}
                    </p>
                    <Badge className="bg-green-600 text-xs">Paid</Badge>
                  </div>
                </div>
                <p className="text-sm text-gray-500 text-center py-4">
                  More billing history will appear here as you continue your subscription
                </p>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No billing history available</p>
            )}
          </CardContent>
        </Card>

        {/* Features Comparison */}
        {!premiumStatus?.isPremium && (
          <Card className="mt-6 bg-gradient-to-r from-red-50 to-pink-50 border-red-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4">Why Upgrade to Premium?</h3>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span>Contact more profiles</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span>Advanced search filters</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span>Priority customer support</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span>Profile highlighting</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SubscriptionDetailsPage;
